/* eslint-disable @typescript-eslint/explicit-module-boundary-types, deprecation/deprecation */
import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { saveAs } from 'file-saver';
import { Observable, asyncScheduler } from 'rxjs';
import { FeedbackService } from '@app/core/services';
import { ReportModeEnum } from '@app/enums';
import { ResponseCodes } from '@app/enums/response-codes.enum';
import { environment } from '@environments/environment';
import { AJSService, AJS_PROVIDERS } from './ajs.service';
import { AuthService } from './auth.service';

@Injectable()
export class ReportService {
	public readonly csvDelimeter = ',';
	public readonly csvNewLine = '\n';
	public readonly cvsQuotSymbol = '"';
	public readonly cvsQuoteEscapeSymbol = '\\"';
	public readonly cvsUnknownValueReplacer = '';

	private readonly contentTypeExcel = 'application/vnd.ms-excel';
	private readonly contentTypeJson = 'application/json';
	private readonly contentTypePdf = 'application/pdf';
	private readonly contentTypeText = 'text/plain';
	private readonly contentTypeCsv = 'text/csv';

	private readonly headerAuthorization = 'Authorization';
	private readonly headerContentType = 'Content-type';

	constructor(
		private readonly ajsService: AJSService,
		private readonly authService: AuthService,
		private readonly feedbackService: FeedbackService,
		private readonly http: HttpClient
	) {}

	private get ajsDialogService(): IAJSDialogService {
		return this.ajsService.getProvider<IAJSDialogService>(AJS_PROVIDERS.DIALOG_HELPER);
	}

	public getReport(scope: any, url: string, params: any, filename: string, onFinishCallback?: (status?: number) => void): void {
		let reportUrl = `${environment.API_URL}${url}`;

		if (params) {
			const reportParam = window.btoa(JSON.stringify(params));
			reportUrl = `${reportUrl}?report=${reportParam}`;
		}

		// Load the PDF via AJAX request. This allows us to check if the request was successful.
		const xhr = new XMLHttpRequest();
		xhr.open('GET', reportUrl, true);
		xhr.setRequestHeader(this.headerAuthorization, this.getBearerToken());
		xhr.responseType = 'blob';

		this.xhronload(xhr, scope, filename, this.contentTypePdf, ReportModeEnum.OpenInTab, onFinishCallback);

		xhr.send();
	}

	public postQueueReport(url: string, params: any, callBackFunction): void {
		const reportUrl = `${environment.API_URL}${url}`;

		// Load the PDF via AJAX request. This allows us to check if the request was successful.
		const xhr = new XMLHttpRequest();
		xhr.open('POST', reportUrl, true);
		xhr.setRequestHeader(this.headerAuthorization, this.getBearerToken());
		xhr.setRequestHeader(this.headerContentType, this.contentTypeJson);

		xhr.onload = () => {
			callBackFunction(xhr.status);
		};

		xhr.send(JSON.stringify(params));
	}

	public postReport(scope: any, url: string, params: any, filename: string, onFinishCallback?: (status?: number) => void): void {
		const reportUrl = `${environment.API_URL}${url}`;

		// Load the PDF via AJAX request. This allows us to check if the request was successful.
		const xhr = new XMLHttpRequest();
		xhr.open('POST', reportUrl, true);
		xhr.setRequestHeader(this.headerAuthorization, this.getBearerToken());
		xhr.setRequestHeader(this.headerContentType, this.contentTypeJson);
		xhr.responseType = 'blob';

		this.xhronload(xhr, scope, filename, this.contentTypePdf, ReportModeEnum.OpenInTab, onFinishCallback);

		xhr.send(JSON.stringify(params));
	}

	public postHfReport(url: string, params: any): Observable<any> {
		return this.http.post(url, params);
	}

	public postReportToModal(scope: any, url: string, params: any, filename: string): void {
		const reportUrl = `${environment.API_URL}${url}`;

		// Load the PDF via AJAX request. This allows us to check if the request was successful.
		const xhr = new XMLHttpRequest();
		xhr.open('POST', reportUrl, true);
		xhr.setRequestHeader(this.headerAuthorization, this.getBearerToken());
		xhr.setRequestHeader(this.headerContentType, this.contentTypeJson);
		xhr.responseType = 'blob';

		this.xhronload(xhr, scope, filename, this.contentTypePdf, ReportModeEnum.OpenInModal);

		xhr.send(JSON.stringify(params));
	}

	public getExcel(scope: any, url: string, params: any, filename: string, onFinishCallback?: (status?: number) => void): void {
		const reportParam = window.btoa(JSON.stringify(params));

		const reportUrl = `${environment.API_URL}${url}?report=${reportParam}`;

		// Load the PDF via AJAX request. This allows us to check if the request was successful.
		const xhr = new XMLHttpRequest();
		xhr.open('GET', reportUrl, true);
		xhr.setRequestHeader(this.headerAuthorization, this.getBearerToken());
		xhr.responseType = 'blob';

		this.xhronload(xhr, scope, filename, this.contentTypeExcel, ReportModeEnum.Download, onFinishCallback);

		xhr.send();
	}

	public postExcel(scope: any, url: string, params: any, filename: string, onFinishCallback?: (status?: number) => void): void {
		const reportUrl = `${environment.API_URL}${url}`;

		// Load the PDF via AJAX request. This allows us to check if the request was successful.
		const xhr = new XMLHttpRequest();
		xhr.open('POST', reportUrl, true);
		xhr.setRequestHeader(this.headerAuthorization, this.getBearerToken());
		xhr.setRequestHeader(this.headerContentType, this.contentTypeJson);
		xhr.responseType = 'blob';

		this.xhronload(xhr, scope, filename, this.contentTypeExcel, ReportModeEnum.Download, onFinishCallback);

		xhr.send(JSON.stringify(params));
	}

	public getTextFile(scope: any, url: string, params: any, filename: string, onFinishCallback?: () => void): void {
		const reportParam = window.btoa(JSON.stringify(params));

		const reportUrl = `${environment.API_URL}${url}?report=${reportParam}`;

		// Load the PDF via AJAX request. This allows us to check if the request was successful.
		const xhr = new XMLHttpRequest();
		xhr.open('GET', reportUrl, true);
		xhr.setRequestHeader(this.headerAuthorization, this.getBearerToken());
		xhr.responseType = 'blob';

		this.xhronload(xhr, scope, filename, this.contentTypeText, ReportModeEnum.Download, onFinishCallback);

		xhr.send();
	}

	public postTextFile(scope: any, url: string, params: any, filename: string): void {
		const reportUrl = `${environment.API_URL}${url}`;

		// Load the PDF via AJAX request. This allows us to check if the request was successful.
		const xhr = new XMLHttpRequest();
		xhr.open('POST', reportUrl, true);
		xhr.setRequestHeader(this.headerAuthorization, this.getBearerToken());
		xhr.setRequestHeader(this.headerContentType, this.contentTypeJson);
		xhr.responseType = 'blob';

		this.xhronload(xhr, scope, filename, this.contentTypeText, ReportModeEnum.Download);

		xhr.send(JSON.stringify(params));
	}

	public downloadBlob(data: any, filename: string, mediaType: string, reportMode: ReportModeEnum): void {
		const blobData = Array.isArray(data) ? data : [data];
		const blob = new Blob(blobData, { type: mediaType });

		if (reportMode === ReportModeEnum.OpenInTab) {
			// IE (as of 11) doesn't allow window.open with Blobs, so we use their native method.
			if ((window.navigator as any).msSaveOrOpenBlob) {
				(window.navigator as any).msSaveOrOpenBlob(blob, filename);
			} else {
				const downloadLink = document.createElement('a');
				const bloburl = URL.createObjectURL(blob);
				downloadLink.href = bloburl;
				downloadLink.download = filename;

				document.body.appendChild(downloadLink);
				downloadLink.click();
				document.body.removeChild(downloadLink);
			}
		} else if (reportMode === ReportModeEnum.OpenInModal) {
			const blobUrl = URL.createObjectURL(blob);
			this.ajsDialogService.reportModal(blobUrl, blob, filename);
		} else if (reportMode === ReportModeEnum.Download) {
			saveAs(blob, filename);
		}
	}

	public downloadCsv(columnsNumber: number, columnHeaders: string[], rows: Array<any>[], filename: string): void {
		const blobData = [];
		if (columnHeaders) {
			if (columnsNumber !== columnHeaders.length) {
				throw new Error('Incorrect number of columns in header');
			}

			columnHeaders.forEach((h) => {
				blobData.push(this.getCsvCell(h));
				blobData.push(this.csvDelimeter);
			});
		}

		blobData.push(this.csvNewLine);
		if (rows && rows.length > 0) {
			rows.forEach((row) => {
				if (columnsNumber !== row.length) {
					throw new Error('Incorrect number of columns in row');
				}

				row.forEach((cellData) => {
					blobData.push(this.getCsvCell(cellData));
					blobData.push(this.csvDelimeter);
				});

				blobData.push(this.csvNewLine);
			});
		}

		const BOM = '\uFEFF';

		blobData.unshift(BOM);

		const blob = new Blob(blobData, {
			type: this.contentTypeCsv,
		});

		this.downloadBlob(blob, filename, this.contentTypeCsv, ReportModeEnum.Download);
	}

	public base64ToBlobData(base64: string): any[] {
		//https://stackoverflow.com/a/20151856/8003721
		const sliceSize = 1024;
		const byteCharacters = window.atob(base64);
		const bytesLength = byteCharacters.length;
		const slicesCount = Math.ceil(bytesLength / sliceSize);
		const byteArrays = new Array(slicesCount);

		for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
			const begin = sliceIndex * sliceSize;
			const end = Math.min(begin + sliceSize, bytesLength);

			const bytes = new Array(end - begin);
			for (let offset = begin, i = 0; offset < end; ++i, ++offset) {
				bytes[i] = byteCharacters[offset].charCodeAt(0);
			}
			byteArrays[sliceIndex] = new Uint8Array(bytes);
		}

		return byteArrays;
	}

	/**
	 * showPrintDialog() - opens the native print dialog for the user to print if applicable.
	 * @param blobData The blob data to print.
	 * @param mimeType The MIME type of the blob data.
	 */
	public showPrintDialog(blobData: any, mimeType: string = 'application/pdf') {
		const isBase64 = (str: string) => {
			const base64Pattern = /^data:([a-zA-Z0-9]+\/[a-zA-Z0-9-.+]+);base64,/;
			return base64Pattern.test(str);
		};

		if (isBase64) {
			blobData = this.base64ToBlobData(blobData);
		}

		blobData = Array.isArray(blobData) ? blobData : [blobData];
		const blob = new Blob(blobData, { type: mimeType });
		const blobUrl = URL.createObjectURL(blob);

		const iframe = document.createElement('iframe');
		iframe.style.display = 'none';
		iframe.src = blobUrl;
		document.body.appendChild(iframe);

		iframe.onload = () => {
			asyncScheduler.schedule(() => {
				const onPrintDialogClose = () => {
					window.removeEventListener('focus', onPrintDialogClose);
					document.body.removeChild(iframe);
					URL.revokeObjectURL(blobUrl);
				};

				window.addEventListener('focus', onPrintDialogClose);
				iframe.contentWindow?.focus();
				iframe.contentWindow?.print();
			}, 500);
		};
	}

	public openFailureDialog(hasNoData: boolean): void {
		const msgList = [];
		if (hasNoData) {
			msgList.push('No data available for this request!');
		} else {
			msgList.push('An error occurred during the request.');
			msgList.push('If the problem continues, please contact your Administrator!');
		}
		this.ajsDialogService.openMessageBox('Report Message', null, msgList, 'Warning', undefined);
	}

	public getCsvCell(data: any): any {
		if (typeof data === 'number') {
			return data;
		}

		if (typeof data !== 'string') {
			return this.cvsUnknownValueReplacer;
		}
		const regex = /"/g;
		return `${this.cvsQuotSymbol}${data.replace(regex, this.cvsQuoteEscapeSymbol)}${this.cvsQuotSymbol}`;
	}

	public responseCodeHandler(status: any, title: string): boolean {
		const contactAdminMessage = 'Please contact your administrator for further assistance.';
		switch (status) {
			case ResponseCodes.Accepted:
				this.feedbackService.success('queued', title);
				return true;
			case ResponseCodes.Unauthorized:
				this.feedbackService.error(`Unauthorized! ${contactAdminMessage}`);
				break;
			default:
				this.feedbackService.error(`An error has occurred! ${contactAdminMessage}`);
				break;
		}

		return false;
	}

	private xhronload(
		xhr: XMLHttpRequest,
		scope: any,
		filename: string,
		mediaType: string,
		reportMode: ReportModeEnum,
		onFinishCallback?: (status?: number) => void
	): void {
		xhr.onload = (): void => {
			if (onFinishCallback) {
				onFinishCallback(xhr.status);
			}
			if (xhr.status === 200) {
				this.downloadBlob(xhr.response, filename, mediaType, reportMode);
			} else if (xhr.status !== 202) {
				this.openFailureDialog(xhr.status === 204);
			}

			scope.isReportFinished = true;
		};
	}

	private getBearerToken(): string {
		let token = this.authService.accessToken;
		if (token) {
			token = `Bearer ${token}`;
		}
		return token;
	}
}

export interface IAJSDialogService {
	openMessageBox(
		messageOrTitle: string,
		messageDetail: string,
		messageDetailArray: string[],
		messageIconType: 'Info' | 'Warning',
		closeFunction: () => any
	): void;

	reportModal(reportUrl: string, blob: any, filename: string): void;
}
